import { distributeIntegerByWeights } from "~/utils/math"

/**
 * MOQ and colli-size rules shared by the Fashion and NOOS supply plans, the PO page and PO
 * creation (SUP-408), so the screens can never disagree about what is a violation.
 *
 * - MOQ (severe) is checked on totals across every warehouse: the SKU MOQ on the SKU total, the
 *   secondary MOQ on the group total. Out-of-scope and synced warehouses count towards it.
 * - Colli size (minor) is checked per SKU×warehouse cell against the SKU's own colli size.
 *   Exempt warehouses (out of scope, synced to pre-orders) never raise a colli violation.
 * - Zero is never a violation: it is either no demand or a deliberate choice not to order.
 *
 * The two resolutions are independent and only ever raise quantities, so they can run in any
 * order: resolving colli never breaks an MOQ, and resolving MOQ ignores colli.
 */

export type QuantityCell = { skuId: string; warehouseId: string; quantity: number }

export type SkuConstraint = {
  skuId: string
  /** Secondary-MOQ group the SKU belongs to (the value of the company's grouping variable). */
  groupKey: string
  moq: number
  secondaryMoq: number
  colliSize: number
}

export type MoqViolation = { amount: number; moq: number }
export type ColliViolation = { amount: number; colliSize: number }

export type QuantityViolations = {
  skuMoq: Map<string, MoqViolation>
  secondaryMoq: Map<string, MoqViolation>
  /** Keyed by {@link cellKey}. */
  colli: Map<string, ColliViolation>
}

export type ViolationOptions = {
  colliExemptWarehouseIds?: ReadonlySet<string>
}

export const cellKey = (skuId: string, warehouseId: string) => `${skuId}|${warehouseId}`

function totalsBySku(cells: readonly QuantityCell[]): Map<string, number> {
  const totals = new Map<string, number>()
  for (const cell of cells) totals.set(cell.skuId, (totals.get(cell.skuId) ?? 0) + cell.quantity)
  return totals
}

function skusByGroup(constraints: readonly SkuConstraint[]): Map<string, SkuConstraint[]> {
  const groups = new Map<string, SkuConstraint[]>()
  for (const c of constraints) {
    const group = groups.get(c.groupKey)
    if (group) group.push(c)
    else groups.set(c.groupKey, [c])
  }
  return groups
}

/** A group's secondary MOQ is the largest any of its SKUs carries. */
function groupSecondaryMoq(group: readonly SkuConstraint[]): number {
  return Math.max(0, ...group.map((c) => c.secondaryMoq))
}

const isShort = (amount: number, moq: number) => amount > 0 && amount < moq

const hasOwnMoq = (c: SkuConstraint) => c.moq > 1

const isOffColli = (quantity: number, colliSize: number) =>
  quantity > 0 && colliSize > 1 && quantity % colliSize !== 0

export function getViolations(
  cells: readonly QuantityCell[],
  constraints: readonly SkuConstraint[],
  options: ViolationOptions = {},
): QuantityViolations {
  const skuTotals = totalsBySku(cells)
  const constraintBySku = new Map(constraints.map((c) => [c.skuId, c]))

  const skuMoq = new Map<string, MoqViolation>()
  for (const c of constraints) {
    const amount = skuTotals.get(c.skuId) ?? 0
    if (isShort(amount, c.moq)) skuMoq.set(c.skuId, { amount, moq: c.moq })
  }

  const secondaryMoq = new Map<string, MoqViolation>()
  for (const [groupKey, group] of skusByGroup(constraints)) {
    const moq = groupSecondaryMoq(group)
    const amount = group.reduce((sum, c) => sum + (skuTotals.get(c.skuId) ?? 0), 0)
    if (isShort(amount, moq)) secondaryMoq.set(groupKey, { amount, moq })
  }

  const colli = new Map<string, ColliViolation>()
  for (const cell of cells) {
    if (options.colliExemptWarehouseIds?.has(cell.warehouseId)) continue
    const colliSize = constraintBySku.get(cell.skuId)?.colliSize ?? 1
    if (isOffColli(cell.quantity, colliSize)) {
      colli.set(cellKey(cell.skuId, cell.warehouseId), { amount: cell.quantity, colliSize })
    }
  }

  return { skuMoq, secondaryMoq, colli }
}

/** Splits `delta` extra units across a SKU's warehouse cells; returns units added per warehouse. */
export type WarehouseSpread = (
  skuId: string,
  cells: readonly QuantityCell[],
  delta: number,
) => Map<string, number>

/**
 * Default spread: in proportion to the current non-zero cells (largest remainder), or evenly when
 * every cell it may use is zero.
 */
export const proportionalWarehouseSpread: WarehouseSpread = (_skuId, cells, delta) => {
  const nonZero = cells.filter((c) => c.quantity > 0)
  const weighted = nonZero.length > 0 ? nonZero : cells
  const shares = distributeIntegerByWeights(
    weighted.map((c) => c.quantity),
    delta,
  )
  return new Map(weighted.map((c, i) => [c.warehouseId, shares[i] ?? 0]))
}

export type ResolveOptions = {
  /** Limit the action to these SKUs; all SKUs when omitted. */
  selectedSkuIds?: ReadonlySet<string>
  /** Cells the action must never change (e.g. committed to a sent PO, or excluded). */
  isCellLocked?: (skuId: string, warehouseId: string) => boolean
}

/**
 * Splits a secondary-MOQ group's shortfall over the group's open cells, zeros included, with any
 * SKU MOQ uplift already on them; returns units added per {@link cellKey}, or null to resolve the
 * group the default way.
 */
export type GroupSpread = (
  cells: readonly QuantityCell[],
  delta: number,
) => Map<string, number> | null

export type ResolveMoqOptions = ResolveOptions & {
  spread?: WarehouseSpread
  groupSpread?: GroupSpread
  /** Warehouses whose quantity is decided elsewhere (e.g. synced to pre-orders): they count towards
   *  the MOQ but never receive the added units. */
  lockedWarehouseIds?: ReadonlySet<string>
}

const isSelected = (skuId: string, options: ResolveOptions) =>
  !options.selectedSkuIds || options.selectedSkuIds.has(skuId)

/**
 * Raises totals to the MOQ: the SKU MOQ first, then the secondary MOQ on the resulting group
 * total, which raising SKUs may already have met. Group shortfall goes to the group's non-zero
 * SKUs in proportion to their totals, or through `groupSpread`, which may also raise a SKU at 0
 * unless that SKU has its own MOQ. Otherwise zeros are never raised. Quantities are never lowered
 * and colli is ignored. Returns every cell, with resolved ones replaced.
 *
 * With a selection, a secondary-MOQ group is only resolved when every ordering SKU in it is
 * selected — resolving a group partly outside the selection is an open product question.
 */
export function resolveMoq<T extends QuantityCell>(
  cells: readonly T[],
  constraints: readonly SkuConstraint[],
  options: ResolveMoqOptions = {},
): T[] {
  const spread = options.spread ?? proportionalWarehouseSpread
  const skuTotals = totalsBySku(cells)
  const targets = new Map(skuTotals)

  for (const c of constraints) {
    const total = targets.get(c.skuId) ?? 0
    if (isSelected(c.skuId, options) && isShort(total, c.moq)) targets.set(c.skuId, c.moq)
  }

  const cellsBySku = new Map<string, T[]>()
  for (const cell of cells) {
    const list = cellsBySku.get(cell.skuId)
    if (list) list.push(cell)
    else cellsBySku.set(cell.skuId, [cell])
  }
  const openCells = (skuId: string) =>
    (cellsBySku.get(skuId) ?? []).filter(
      (c) =>
        !options.lockedWarehouseIds?.has(c.warehouseId) &&
        !options.isCellLocked?.(c.skuId, c.warehouseId),
    )
  const spreadOverWarehouses = (skuId: string, delta: number, into: Map<string, number>) => {
    if (delta <= 0) return
    const open = openCells(skuId)
    if (open.length === 0) return
    for (const [warehouseId, units] of spread(skuId, open, delta)) {
      const key = cellKey(skuId, warehouseId)
      into.set(key, (into.get(key) ?? 0) + units)
    }
  }

  const added = new Map<string, number>()
  const spreadByGroup = new Set<string>()
  // False when `groupSpread` declines, leaving the group to the default split.
  const applyGroupSpread = (group: readonly SkuConstraint[], shortfall: number): boolean => {
    if (!options.groupSpread) return false
    const skuUplift = new Map<string, number>()
    for (const c of group) {
      spreadOverWarehouses(
        c.skuId,
        (targets.get(c.skuId) ?? 0) - (skuTotals.get(c.skuId) ?? 0),
        skuUplift,
      )
    }
    // A SKU at 0 with its own MOQ stays out: a few units on it would break that MOQ.
    const open = group
      .filter(
        (c) => isSelected(c.skuId, options) && ((targets.get(c.skuId) ?? 0) > 0 || !hasOwnMoq(c)),
      )
      .flatMap((c) => openCells(c.skuId))
      .map((c) => ({
        skuId: c.skuId,
        warehouseId: c.warehouseId,
        quantity: c.quantity + (skuUplift.get(cellKey(c.skuId, c.warehouseId)) ?? 0),
      }))
    const groupUnits = options.groupSpread(open, shortfall)
    if (!groupUnits) return false
    for (const units of [skuUplift, groupUnits]) {
      for (const [key, n] of units) if (n > 0) added.set(key, (added.get(key) ?? 0) + n)
    }
    for (const c of group) spreadByGroup.add(c.skuId)
    return true
  }

  for (const group of skusByGroup(constraints).values()) {
    const moq = groupSecondaryMoq(group)
    const ordering = group.filter((c) => (targets.get(c.skuId) ?? 0) > 0)
    const amount = ordering.reduce((sum, c) => sum + (targets.get(c.skuId) ?? 0), 0)
    if (!isShort(amount, moq)) continue
    if (!ordering.every((c) => isSelected(c.skuId, options))) continue
    if (applyGroupSpread(group, moq - amount)) continue
    const shares = distributeIntegerByWeights(
      ordering.map((c) => targets.get(c.skuId) ?? 0),
      moq - amount,
    )
    ordering.forEach((c, i) => targets.set(c.skuId, (targets.get(c.skuId) ?? 0) + (shares[i] ?? 0)))
  }

  for (const skuId of cellsBySku.keys()) {
    if (spreadByGroup.has(skuId)) continue
    spreadOverWarehouses(skuId, (targets.get(skuId) ?? 0) - (skuTotals.get(skuId) ?? 0), added)
  }

  if (added.size === 0) return [...cells]
  return cells.map((cell) => {
    const units = added.get(cellKey(cell.skuId, cell.warehouseId))
    return units ? { ...cell, quantity: cell.quantity + units } : cell
  })
}

/**
 * Secondary-MOQ groups that are short but only partly selected, which {@link resolveMoq} skips.
 * Same rule as there: a group counts as selected when every SKU in it with a quantity is.
 */
export function partiallySelectedShortGroups(
  cells: readonly QuantityCell[],
  constraints: readonly SkuConstraint[],
  selectedSkuIds: ReadonlySet<string>,
): string[] {
  const skuTotals = totalsBySku(cells)
  const out: string[] = []
  for (const [groupKey, group] of skusByGroup(constraints)) {
    const ordering = group.filter((c) => (skuTotals.get(c.skuId) ?? 0) > 0)
    const amount = ordering.reduce((sum, c) => sum + (skuTotals.get(c.skuId) ?? 0), 0)
    if (!isShort(amount, groupSecondaryMoq(group))) continue
    const selected = ordering.filter((c) => selectedSkuIds.has(c.skuId)).length
    if (selected > 0 && selected < ordering.length) out.push(groupKey)
  }
  return out
}

export type ResolveColliOptions = ResolveOptions & ViolationOptions

/** Rounds every off-colli cell up to the next multiple of its SKU's colli size. MOQ is ignored. */
export function resolveColli<T extends QuantityCell>(
  cells: readonly T[],
  constraints: readonly SkuConstraint[],
  options: ResolveColliOptions = {},
): T[] {
  const colliBySku = new Map(constraints.map((c) => [c.skuId, c.colliSize]))
  return cells.map((cell) => {
    if (!isSelected(cell.skuId, options)) return cell
    if (options.colliExemptWarehouseIds?.has(cell.warehouseId)) return cell
    if (options.isCellLocked?.(cell.skuId, cell.warehouseId)) return cell
    const colliSize = colliBySku.get(cell.skuId) ?? 1
    if (!isOffColli(cell.quantity, colliSize)) return cell
    return { ...cell, quantity: Math.ceil(cell.quantity / colliSize) * colliSize }
  })
}

/** Violations indexed for table cells: per SKU, per group, per SKU×warehouse. */
export type ViolationIndex = QuantityViolations & {
  groupKeyBySku: ReadonlyMap<string, string>
  /** skuId -> warehouseId -> colli violation. */
  colliBySku: ReadonlyMap<string, ReadonlyMap<string, ColliViolation>>
}

export function indexViolations(
  cells: readonly QuantityCell[],
  constraints: readonly SkuConstraint[],
  options: ViolationOptions = {},
): ViolationIndex {
  const violations = getViolations(cells, constraints, options)
  const colliBySku = new Map<string, Map<string, ColliViolation>>()
  for (const cell of cells) {
    const violation = violations.colli.get(cellKey(cell.skuId, cell.warehouseId))
    if (!violation) continue
    const perWarehouse = colliBySku.get(cell.skuId) ?? new Map<string, ColliViolation>()
    perWarehouse.set(cell.warehouseId, violation)
    colliBySku.set(cell.skuId, perWarehouse)
  }
  return {
    ...violations,
    groupKeyBySku: new Map(constraints.map((c) => [c.skuId, c.groupKey])),
    colliBySku,
  }
}

/** A {@link ViolationIndex} plus the variants with an MOQ or colli violation, for the badge counts. */
export type VariantViolationIndex = ViolationIndex & {
  moqVariantIds: ReadonlySet<string>
  colliVariantIds: ReadonlySet<string>
}

export function indexVariantViolations(
  cells: readonly QuantityCell[],
  constraints: readonly SkuConstraint[],
  variantIdBySku: ReadonlyMap<string, string>,
  options: ViolationOptions = {},
): VariantViolationIndex {
  const index = indexViolations(cells, constraints, options)
  const moqVariantIds = new Set<string>()
  for (const [skuId, groupKey] of index.groupKeyBySku) {
    const variantId = variantIdBySku.get(skuId)
    if (variantId && (index.skuMoq.has(skuId) || index.secondaryMoq.has(groupKey))) {
      moqVariantIds.add(variantId)
    }
  }
  const colliVariantIds = new Set<string>()
  for (const skuId of index.colliBySku.keys()) {
    const variantId = variantIdBySku.get(skuId)
    if (variantId) colliVariantIds.add(variantId)
  }
  return { ...index, moqVariantIds, colliVariantIds }
}

export function hasMoqViolation(index: ViolationIndex, skuIds: readonly string[]): boolean {
  return skuIds.some((skuId) => {
    if (index.skuMoq.has(skuId)) return true
    const groupKey = index.groupKeyBySku.get(skuId)
    return groupKey !== undefined && index.secondaryMoq.has(groupKey)
  })
}

/** Any colli violation on these SKUs — in one warehouse, or in any when `warehouseId` is omitted. */
export function hasColliViolation(
  index: ViolationIndex,
  skuIds: readonly string[],
  warehouseId?: string,
): boolean {
  return skuIds.some((skuId) => {
    const perWarehouse = index.colliBySku.get(skuId)
    if (!perWarehouse) return false
    return warehouseId === undefined || perWarehouse.has(warehouseId)
  })
}

/** The SKU's value of the company's secondary-MOQ grouping field, which is free text naming a SKU field. */
export function skuGroupKey(sku: object, groupVariable: string): string {
  const value: unknown = Reflect.get(sku, groupVariable)
  return value == null ? "" : String(value)
}

/** {@link SkuConstraint}s from the per-SKU settings `sku.getDataForPOs` returns. */
export function constraintsFromSkuSettings(
  skus: readonly {
    skuId: string
    settings?: {
      minimumOrderQuantity?: number | null
      secondaryMoq?: number | null
      colliSize?: number | null
    } | null
  }[],
  groupVariable: string,
  companyColliSize: number | null | undefined,
): SkuConstraint[] {
  return skus.map((sku) => ({
    skuId: sku.skuId,
    groupKey: skuGroupKey(sku, groupVariable),
    moq: sku.settings?.minimumOrderQuantity ?? 1,
    secondaryMoq: sku.settings?.secondaryMoq ?? 0,
    colliSize: sku.settings?.colliSize ?? companyColliSize ?? 1,
  }))
}

/** skuId -> warehouseId -> new quantity, for every cell a resolution changed. */
export function changedCells(
  before: readonly QuantityCell[],
  after: readonly QuantityCell[],
): Map<string, Map<string, number>> {
  const updates = new Map<string, Map<string, number>>()
  before.forEach((cell, i) => {
    const next = after[i]
    if (!next || next.quantity === cell.quantity) return
    const perWarehouse = updates.get(cell.skuId) ?? new Map<string, number>()
    perWarehouse.set(cell.warehouseId, next.quantity)
    updates.set(cell.skuId, perWarehouse)
  })
  return updates
}
